import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useMutation, useQueryClient } from "react-query";
import ModalHeader from "react-bootstrap/esm/ModalHeader";
import { Modal, ModalBody } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";

import { getSensor, getAllSensors } from "@modules/Sensors/services/sensorCrud";
import { addInstallation, updateInstallation } from "../../../../../services/treeCrud";

const toDateInputValue = (value) => {
  if (!value) return "";
  const stringValue = String(value);
  return stringValue.includes("T") ? stringValue.split("T")[0] : stringValue.slice(0, 10);
};

// Treat an installation as active when removed_at is falsy.
const isActiveInstallation = (inst) => inst && !inst.removed_at;

// ID of the sensor attached to the installation being edited (update mode).
const getCurrentInstallationSensorId = (installationItem) => installationItem?.sensor?.id ?? null;

// Set of sensor IDs already actively installed on THIS tree,
// excluding the installation currently being edited.
const getBlockedSensorIds = (currentInstallations, installationItem) => {
  const editingId = getCurrentInstallationSensorId(installationItem);
  const ids = new Set();
  (currentInstallations || []).filter(isActiveInstallation).forEach((inst) => {
    const sid = inst.sensor?.id;
    if (sid != null && sid !== editingId) ids.add(sid);
  });
  return ids;
};

const isSensorBlocked = (sensorId, blockedIds) => sensorId != null && blockedIds.has(sensorId);

// Filter a list of { value, label } options against blockedIds.
const filterSelectableSensors = (options, blockedIds) =>
  (options || []).filter((opt) => !blockedIds.has(opt.value));

export default function InstallationForm({
  show,
  renderMode = "modal",
  tId,
  setOpenForm,
  isUpdate,
  setIsUpdate,
  installationItem,
  setInstallationItem,
  sensors,
  addedSensor,
  setFieldValue,
  currentInstallations,
  prefillHeight,
  externalHeight,
  hideHeightField = false,
  onSensorTypeChange,
  externalDepth,
  onDepthChange,
  forceSoilMode = false,
  onHeightChange,
}) {
  const isInline = renderMode === "inline";
  // Helper function to resolve sensor name from multiple sources
  const resolveSensorName = (installation, sensorId) => {
    // Priority: API response > existing installation > sensors list
    return (
      installation.sensor?.name ||
      currentInstallations?.find((inst) => inst.sensor?.id === sensorId)?.sensor?.name ||
      sensors?.find((s) => s.id === sensorId)?.name
    );
  };
  const getValueLabel = (items, value) => {
    const item = items.find((item) => item.value === value);
    return item ? item.name : null; // Return the label or null if not found
  };
  const { t } = useTranslation();
  const [defaultSensor, setDefaultSensor] = useState(null);
  const [sensorId, setSensorId] = useState(null);
  const [sensorError, setSensorError] = useState("");

  const blockedSensorIds = useMemo(
    () => getBlockedSensorIds(currentInstallations, installationItem),
    [currentInstallations, installationItem]
  );
  const sensorTypeCallback = useMemo(
    () => (typeof onSensorTypeChange === "function" ? onSensorTypeChange : null),
    [onSensorTypeChange]
  );
  useEffect(() => {
    setDefaultSensor(
      installationItem
        ? {
            value: installationItem.sensor.id,
            label: installationItem.sensor.name,
          }
        : addedSensor
        ? {
            value: addedSensor.id,
            label: addedSensor.name,
          }
        : null
    );
  }, [addedSensor, installationItem, sensorId]);
  const queryClient = useQueryClient();
  const depths = [
    {
      name: "30 cm",
      value: 30,
    },
    {
      name: "60 cm",
      value: 60,
    },
    {
      name: "90 cm",
      value: 90,
    },
    {
      name: "120 cm",
      value: 120,
    },
    {
      name: t("root_ball", { defaultValue: "Ballen" }),
      value: 0,
    },
  ];
  const getDepthId = (depthValue) => {
    if (depthValue == null || depthValue === "") return null;
    if (typeof depthValue === "object" && depthValue.id != null) {
      return Number(depthValue.id);
    }
    const parsed = Number(depthValue);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleClose = () => {
    setSensorError("");
    if (typeof setOpenForm === "function") {
      setOpenForm(false);
    }
    if (typeof setIsUpdate === "function") {
      setIsUpdate(false);
    }
    if (typeof setInstallationItem === "function") {
      setInstallationItem(null);
    }
  };
  const { isLoading, mutateAsync } = useMutation(isUpdate ? updateInstallation : addInstallation, {
    onSuccess: (data) => {
      setSensorError("");
      // Update form values directly for immediate UI update
      if (setFieldValue && currentInstallations) {
        let updatedInstallations;
        if (isUpdate) {
          // Update existing installation - use helper function to resolve sensor name
          updatedInstallations = currentInstallations.map((inst) => {
            if (inst.id === data.id) {
              return {
                ...data,
                sensor: {
                  ...data.sensor,
                  name: resolveSensorName(data, data.sensor?.id),
                },
              };
            }
            return inst;
          });
        } else {
          // Add new installation - use helper function to resolve sensor name
          const newInstallation = {
            ...data,
            sensor: {
              ...data.sensor,
              name: resolveSensorName(data, data.sensor?.id),
            },
          };
          updatedInstallations = [...currentInstallations, newInstallation];
        }
        setFieldValue("installations", updatedInstallations);
      }

      // Update project cache to reflect installation changes in tree_set
      const projectQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === "project",
      });
      projectQueries.forEach(([queryKey, projectData]) => {
        if (projectData) {
          queryClient.setQueryData(queryKey, (oldProject) => {
            if (!oldProject) return oldProject;

            return {
              ...oldProject,
              areas:
                oldProject.areas?.map((area) => ({
                  ...area,
                  tree_set:
                    area.tree_set?.map((tree) => {
                      if (tree.id === tId) {
                        let updatedInstallations;
                        if (isUpdate) {
                          // Update existing installation in tree - use helper function to resolve sensor name
                          updatedInstallations =
                            tree.installations?.map((inst) =>
                              inst.id === data.id
                                ? {
                                    ...data,
                                    sensor: {
                                      ...data.sensor,
                                      name: resolveSensorName(data, data.sensor?.id),
                                    },
                                  }
                                : inst
                            ) || [];
                        } else {
                          // Add new installation to tree - use helper function to resolve sensor name
                          const newInstallation = {
                            ...data,
                            sensor: {
                              ...data.sensor,
                              name: resolveSensorName(data, data.sensor?.id),
                            },
                          };
                          updatedInstallations = [...(tree.installations || []), newInstallation];
                        }
                        return {
                          ...tree,
                          installations: updatedInstallations,
                        };
                      }
                      return tree;
                    }) || [],
                })) || [],
            };
          });
        }
      });

      // Still invalidate queries for other components
      const treeIdStr = tId.toString();
      const treeIdNum = parseInt(tId);
      queryClient.invalidateQueries(["tree", treeIdStr]);
      queryClient.invalidateQueries(["tree", treeIdNum]);
      queryClient.invalidateQueries("tree");
      queryClient.invalidateQueries("trees");
      queryClient.invalidateQueries("project");
      queryClient.invalidateQueries("possibleChildTrees");
      queryClient.invalidateQueries("possibleParentTrees");

      // Push fresh installation into tree-level caches so UI updates immediately
      const mergeInstallation = (list = []) => {
        const exists = list.some((inst) => inst.id === data.id);
        if (exists) {
          return list.map((inst) => (inst.id === data.id ? data : inst));
        }
        return [...list, data];
      };

      queryClient.setQueryData(["tree", treeIdStr], (prev) =>
        prev ? { ...prev, installations: mergeInstallation(prev.installations || []) } : prev
      );
      queryClient.setQueryData(["tree", treeIdNum], (prev) =>
        prev ? { ...prev, installations: mergeInstallation(prev.installations || []) } : prev
      );
      const tIdNum = Number(tId);
      queryClient.setQueryData("trees", (prev) => {
        if (!prev) return prev;
        if (Array.isArray(prev)) {
          return prev.map((t) =>
            Number(t.id) === tIdNum
              ? { ...t, installations: mergeInstallation(t.installations || []) }
              : t
          );
        }
        if (prev.results) {
          return {
            ...prev,
            results: prev.results.map((t) =>
              Number(t.id) === tIdNum
                ? { ...t, installations: mergeInstallation(t.installations || []) }
                : t
            ),
          };
        }
        return prev;
      });

      handleClose();
    },
    onError: (error) => {
      const data = error?.response?.data;
      const sensorFieldError = Array.isArray(data?.sensor_id) ? data.sensor_id[0] : data?.sensor_id;
      const detailError = Array.isArray(data?.detail) ? data.detail[0] : data?.detail;
      setSensorError(
        sensorFieldError ||
          detailError ||
          t("installation_save_failed", {
            defaultValue: "Could not save installation. Please try again.",
          })
      );
    },
  });

  useEffect(() => {
    if (addedSensor) {
      setSensorId(addedSensor.id);
    }
  }, [addedSensor]);

  var initialValues = isUpdate
    ? {
        ...installationItem,
        installed_at: toDateInputValue(installationItem?.installed_at),
        removed_at: toDateInputValue(installationItem?.removed_at),
      }
    : {
        sensor: {},
        depth: "",
        height: prefillHeight ?? "",
        tree: { type: "Tree", id: tId },
      };
  const { data: sensorObj } = useQuery(["sensors", { sensorId }], () => getSensor(sensorId), {
    refetchOnWindowFocus: false,
    retry: 3, // number of times to retry the request before giving up
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // delay between retries in milliseconds
    enabled: !!sensorId,
  });
  const getSensorType = (sensor) => {
    if (!sensor) return null;
    if (sensor.dendrometer_sensor) return "dendrometer";
    if (sensor.soil_moisture_sensor) return "soil_moisture";
    if (sensor.soil_water_tension_sensor) return "soil_water_tension";
    if (sensor.pulse_sensor) return "pulse";
    if (sensor.tilt_sensor) return "tilt";
    const lowerName = sensor.name ? sensor.name.toLowerCase() : "";
    if (lowerName.includes("dendro")) return "dendrometer";
    if (lowerName.includes("tensi") || lowerName.includes("tension")) return "soil_water_tension";
    if (lowerName.includes("moist")) return "soil_moisture";
    if (lowerName.includes("tilt") || lowerName.includes("neigung")) return "tilt";
    if (sensor.sensor_type) return sensor.sensor_type; // fallback if backend sends a type string
    return "pulse";
  };

  // Determine sensor type also from list/installation when no sensorObj yet
  const selectedSensor = useMemo(
    () => sensorObj || sensors?.find((s) => s.id === sensorId) || installationItem?.sensor || null,
    [sensorObj, sensors, sensorId, installationItem]
  );

  const sensorType = useMemo(() => getSensorType(selectedSensor), [selectedSensor]);
  const isSoilSensorType = useMemo(() => {
    if (forceSoilMode) return true;
    return sensorType === "soil_moisture" || sensorType === "soil_water_tension";
  }, [sensorType, forceSoilMode]);

  useEffect(() => {
    if (sensorTypeCallback) {
      sensorTypeCallback(sensorType);
    }
  }, [sensorType, sensorTypeCallback]);

  // When sensor is loaded and type is determined, adjust depth field if needed
  useEffect(() => {
    if (selectedSensor && sensorType && !isUpdate) {
      // Only auto-adjust for new installations, not edits
      const isSoil = sensorType === "soil_moisture" || sensorType === "soil_water_tension";
      if (isSoil && !initialValues?.depth?.id) {
        // Auto-set default depth for soil sensors
        // Note: This would need setFieldValue from Formik context, which we don't have here
        // The depth adjustment will happen in the onChange handler instead
      }
    }
  }, [selectedSensor, sensorType, isUpdate]);

  // Determine sensor type filter based on mode
  const sensorTypeFilter = forceSoilMode ? "soil" : "crown";

  // Load sensors with server-side search. Also filters blocked sensors client-side
  // so same-tree duplicates / stale backend responses cannot appear.
  const loadSensorOptions = useCallback(
    async (inputValue) => {
      try {
        const response = await getAllSensors({
          search: inputValue,
          available_for_installation: "true",
          sensor_type: sensorTypeFilter, // Filter by crown or soil sensors
          pageSize: 50, // Limit to 50 results for performance
        });
        const sensors = response?.results || [];
        const options = sensors.map((sensor) => ({
          value: sensor.id,
          label: sensor.name,
        }));
        return filterSelectableSensors(options, blockedSensorIds);
      } catch (error) {
        console.error("Error loading sensors:", error);
        return [];
      }
    },
    [blockedSensorIds, sensorTypeFilter]
  );

  const formContent = (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values) => {
        // Ensure we have a valid sensor ID

        const sensorId = values.sensor?.id || addedSensor?.id;

        if (!sensorId) {
          setSensorError(t("please_select_sensor", { defaultValue: "Please select a sensor." }));
          return;
        }

        const formdata = {
          sensor_id: sensorId, // Send sensor_id directly instead of nested object
          tree_id: tId, // Send tree_id directly instead of nested object
        };
        if (isUpdate) {
          formdata.installed_at = values.installed_at || null;
          formdata.removed_at = values.removed_at || null;
        } else {
          // Auto-set timestamp: installation happens now (date-only per backend model)
          formdata.installed_at = new Date().toISOString().split("T")[0];
        }
        let data = { formdata: formdata };
        isUpdate ? (data.id = values.id) : null;
        const shouldSubmitHeight = !forceSoilMode && !isSoilSensorType;
        if (shouldSubmitHeight) {
          const heightSource =
            externalHeight !== undefined && externalHeight !== null && externalHeight !== ""
              ? externalHeight
              : values.height;
          if (heightSource !== undefined && heightSource !== null && heightSource !== "") {
            const normalizedHeight = Math.round(Number(String(heightSource).replace(",", ".")));
            if (!Number.isNaN(normalizedHeight)) {
              formdata.height = normalizedHeight;
            }
          }
        }
        const depthSource =
          externalDepth !== undefined && externalDepth !== null
            ? Number(externalDepth)
            : getDepthId(values.depth);
        if (
          (forceSoilMode || isSoilSensorType) &&
          depthSource !== undefined &&
          depthSource !== null
        ) {
          formdata.depth = depthSource;
        }
        try {
          setSensorError("");
          await mutateAsync(data);
        } catch {
          // handled in onError
        }
      }}
    >
      {({ setFieldValue, values }) => (
        <Form>
          <div className={isInline ? "row g-2" : undefined}>
            <div className={isInline ? "col-12" : undefined}>
              <div className={isInline ? "d-flex align-items-end gap-2" : undefined}>
                <div className={isInline ? "flex-grow-1" : undefined}>
                  <label htmlFor="">{t("sensor")}</label>
                  {sensors && sensors.length > 0 ? (
                    <Select
                      classNamePrefix="react-select"
                      id={`sensor-installation-form`}
                      type={"text"}
                      defaultValue={defaultSensor}
                      value={
                        values.sensor.id
                          ? {
                              value: values.sensor.id,
                              label: values.sensor.name,
                            }
                          : defaultSensor
                      }
                      placeholder={t("select_sensor")}
                      onChange={(option) => {
                        const id = parseInt(option.value);
                        if (isSensorBlocked(id, blockedSensorIds)) {
                          setSensorError(
                            t("sensor_already_installed", {
                              defaultValue: "This sensor is already actively installed.",
                            })
                          );
                          return;
                        }
                        setSensorError("");
                        setSensorId(id);
                        setFieldValue("sensor", { id: id, name: option.label });

                        // Adjust fields dynamically based on sensor type
                        const selected = sensors?.find((s) => s.id === id);
                        const type = getSensorType(selected);
                        if (type === "soil_moisture" || type === "soil_water_tension") {
                          if (!values.depth?.id) {
                            setFieldValue("depth", { id: 30 });
                          }
                        } else {
                          // non-soil sensors -> clear depth
                          setFieldValue("depth", null);
                        }
                      }}
                      isMulti={false}
                      options={filterSelectableSensors(
                        sensors?.map((sensor) => ({
                          value: sensor.id,
                          label: sensor.name,
                        })),
                        blockedSensorIds
                      )}
                    />
                  ) : (
                    <AsyncSelect
                      classNamePrefix="react-select"
                      key={`sensor-select-${sensorTypeFilter}`}
                      id={`sensor-installation-form`}
                      cacheOptions
                      defaultOptions
                      loadOptions={loadSensorOptions}
                      defaultValue={defaultSensor}
                      value={
                        values.sensor.id
                          ? {
                              value: values.sensor.id,
                              label: values.sensor.name,
                            }
                          : defaultSensor
                      }
                      placeholder={t("select_sensor_search", {
                        defaultValue: "Search for sensor...",
                      })}
                      onChange={async (option) => {
                        if (!option) {
                          // Cleared selection
                          setSensorError("");
                          setSensorId(null);
                          setFieldValue("sensor", {});
                          setFieldValue("depth", null);
                          return;
                        }

                        const id = parseInt(option.value);
                        if (isSensorBlocked(id, blockedSensorIds)) {
                          setSensorError(
                            t("sensor_already_installed", {
                              defaultValue: "This sensor is already actively installed.",
                            })
                          );
                          return;
                        }
                        setSensorError("");
                        setSensorId(id);
                        setFieldValue("sensor", { id: id, name: option.label });

                        // Fetch sensor details to determine type and adjust fields
                        try {
                          const sensor = await getSensor(id);
                          const type = getSensorType(sensor);
                          if (type === "soil_moisture" || type === "soil_water_tension") {
                            if (!values.depth?.id) {
                              setFieldValue("depth", { id: 30 });
                            }
                          } else {
                            setFieldValue("depth", null);
                          }
                        } catch (error) {
                          console.error("Error fetching sensor details:", error);
                        }
                      }}
                      isMulti={false}
                      isClearable
                      noOptionsMessage={({ inputValue }) =>
                        inputValue ? t("no_sensors_found") : t("start_typing_to_search")
                      }
                    />
                  )}
                  {sensorError && (
                    <div className="text-danger small mt-1" role="alert">
                      {sensorError}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {isSoilSensorType && (
              <div className={isInline ? "col-12 col-md-6" : undefined}>
                <label htmlFor="">{t("depth")}</label>
                <Select
                  classNamePrefix="react-select"
                  id={`sensor-installation-depth`}
                  type={"text"}
                  placeholder={t("select_depth")}
                  onChange={(option) => {
                    const nextDepth = parseInt(option.value);
                    setFieldValue("depth", { id: nextDepth });
                    if (typeof onDepthChange === "function") {
                      onDepthChange(nextDepth);
                    }
                  }}
                  value={(() => {
                    const hasExternalDepth = externalDepth !== undefined && externalDepth !== null;
                    const depthValue = hasExternalDepth ? externalDepth : values.depth?.id;
                    if (depthValue === undefined || depthValue === null) return null;
                    return {
                      value: depthValue,
                      label: getValueLabel(depths, depthValue),
                    };
                  })()}
                  options={depths?.map((depth) => ({
                    value: depth.value,
                    label: depth.name,
                  }))}
                  defaultValue={
                    installationItem
                      ? {
                          value: installationItem.depth,
                          label: getValueLabel(depths, installationItem.depth)
                            ? getValueLabel(depths, installationItem.depth)
                            : null,
                        }
                      : null
                  }
                />
              </div>
            )}
            {!isSoilSensorType && !hideHeightField && (
              <div className={isInline ? "col-12 col-md-6" : undefined}>
                <label htmlFor="sensor-installation-height">{t("height")} (m)</label>
                <input
                  id="sensor-installation-height"
                  type="number"
                  inputMode="decimal"
                  className="form-control"
                  min="1"
                  step="1"
                  value={values.height ?? ""}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setFieldValue("height", nextValue);
                    if (typeof onHeightChange === "function") {
                      onHeightChange(nextValue);
                    }
                  }}
                />
              </div>
            )}
            {isUpdate && (
              <>
                <div className={isInline ? "col-12 col-md-6" : undefined}>
                  <label htmlFor="sensor-installed-at">{t("installed_at")}</label>
                  <input
                    id="sensor-installed-at"
                    type="date"
                    className="form-control"
                    value={values.installed_at || ""}
                    onChange={(event) => setFieldValue("installed_at", event.target.value)}
                  />
                </div>
                <div className={isInline ? "col-12 col-md-6" : undefined}>
                  <label htmlFor="sensor-removed-at">{t("removed_at")}</label>
                  <input
                    id="sensor-removed-at"
                    type="date"
                    className="form-control"
                    value={values.removed_at || ""}
                    onChange={(event) => setFieldValue("removed_at", event.target.value)}
                  />
                </div>
              </>
            )}
            <div className={isInline ? "col-12" : undefined}>
              <button type="submit" className="btn btn-outline-primary">
                {isLoading ? t("saving") : t("save")}
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );

  if (isInline) {
    if (show === false) return null;
    return <div className="installation-form-inline">{formContent}</div>;
  }

  return (
    <Modal
      className="installation-form-modal"
      data-testid="maintenance-form-modal"
      onHide={handleClose}
      show={show}
      centered
    >
      <ModalHeader closeButton>
        <h5 className="mb-0">
          {isUpdate ? `${t("edit")} ${t("installation")}` : `${t("add_installation")}`}
        </h5>
      </ModalHeader>
      <ModalBody>{formContent}</ModalBody>
    </Modal>
  );
}
